import { HttpClient } from '@angular/common/http';
import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { TimerSessionDto } from '../models/pipeline';

/**
 * Phase 1 (SPEC v2 §6.8 Step 3) — bottom-bar timer.
 *
 * The service owns three concerns:
 *  1. The thin HTTP layer over /api/timer/{start,stop,current}.
 *  2. The "currently running" session as a signal, kept in sync via realtime
 *     pushes (RealtimeService wires `timerStarted` / `timerStopped` events
 *     into {@link applyStarted} / {@link applyStopped}).
 *  3. A 1Hz tick that updates `elapsedSeconds` while a session is active so
 *     the bar's running display moves without re-rendering the page.
 *
 * The tick is started lazily on the first active session and torn down when
 * no session is running, so an idle app does no work. `setInterval` is fine
 * here — we are not chasing sub-second accuracy; the visible value snaps to
 * the server's `startedAt` on every realtime push.
 */
@Injectable({ providedIn: 'root' })
export class TimerService {
  private readonly http = inject(HttpClient);
  private readonly base = environment.apiBaseUrl.replace(/\/+$/, '');

  private readonly _active = signal<TimerSessionDto | null>(null);
  private readonly _nowMs = signal<number>(Date.now());

  readonly active = this._active.asReadonly();

  /** Live elapsed seconds for the currently active session — 0 when idle. */
  readonly elapsedSeconds = computed(() => {
    const a = this._active();
    if (!a) return 0;
    const startedMs = Date.parse(a.startedAt);
    const nowMs = this._nowMs();
    return Math.max(0, Math.floor((nowMs - startedMs) / 1000));
  });

  private tickHandle?: ReturnType<typeof setInterval>;

  constructor() {
    // Stop the interval if the service is ever destroyed (test scaffolding).
    // In a real app the root injector lives for the session.
    const destroyRef = inject(DestroyRef, { optional: true });
    if (destroyRef) {
      destroyRef.onDestroy(() => this.stopTick());
    }
  }

  async loadCurrent(): Promise<TimerSessionDto | null> {
    const session = await firstValueFrom(
      this.http.get<TimerSessionDto | null>(`${this.base}/api/timer/current`, {
        observe: 'response',
      }),
    ).then(r => r.status === 204 ? null : (r.body ?? null));
    this.setActive(session);
    return session;
  }

  async start(actionItemId: string, notes?: string | null): Promise<TimerSessionDto> {
    const session = await firstValueFrom(
      this.http.post<TimerSessionDto>(
        `${this.base}/api/timer/start`,
        { actionItemId, notes: notes ?? null },
      ),
    );
    this.setActive(session);
    return session;
  }

  async stop(notes?: string | null): Promise<TimerSessionDto | null> {
    const resp = await firstValueFrom(
      this.http.post<TimerSessionDto | null>(
        `${this.base}/api/timer/stop`,
        { notes: notes ?? null },
        { observe: 'response' },
      ),
    );
    this.setActive(null);
    return resp.status === 204 ? null : (resp.body ?? null);
  }

  // -------- Realtime hooks --------

  applyStarted(session: TimerSessionDto): void {
    this.setActive(session);
  }

  applyStopped(_session: TimerSessionDto): void {
    // A stop from any tab clears the active session locally — even if the
    // payload is some session we didn't know about, the server has decided
    // nothing is running for this owner, so we trust that.
    this.setActive(null);
  }

  private setActive(s: TimerSessionDto | null): void {
    this._active.set(s);
    this._nowMs.set(Date.now());
    if (s) this.startTick(); else this.stopTick();
  }

  private startTick(): void {
    if (this.tickHandle !== undefined) return;
    this.tickHandle = setInterval(() => this._nowMs.set(Date.now()), 1000);
  }

  private stopTick(): void {
    if (this.tickHandle !== undefined) {
      clearInterval(this.tickHandle);
      this.tickHandle = undefined;
    }
  }
}

/** "1:23" / "12:04" / "1:02:08" — mm:ss for under an hour, h:mm:ss otherwise. */
export function formatElapsed(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${mm}:${pad(ss)}`;
}
